import React, {
  ChangeEventHandler,
  FC,
  useCallback,
  MouseEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'react-bootstrap';
import ReactToPrint from 'react-to-print';
import { TableColumn } from 'modules/core/components/Table/types';
import { STATIC_IMAGE_URLS } from 'modules/core/constants';
import { ButtonVariant, ColumnWidth, DealSourceProvider } from 'modules/core/enums';
import { Panel, Toggle } from 'modules/core/components';
import { IField, IOption, IUser } from 'modules/core/types';
import { useIsAmeriCreditLender } from 'modules/core/hooks/useIsAmeriCreditLender';
import { useIsMtbLender } from 'modules/core/hooks/useIsMtbLender';
import { useSelector } from 'react-redux';
import { getIsMissingCostInfo } from 'store/selectors/ui.selector';
import { getUser } from 'store/selectors/auth.selector';
import { getVehiclePhotoSrc } from 'modules/core/utils';
import { getDefaultVehiclePhoto } from 'store/selectors/vehicle.selector';
import { getControlImages } from 'store/selectors/controls.selector';
import { AddendumStatus } from 'modules/core/enums/addendumStatus.enum';

import {
  Img,
  ImgContainer,
  VehiclesDescriptionFields,
  FieldsRow,
  PaymentCol,
  PaymentRow,
  PanelTitle,
  ActualPaymentInfoTitle,
  VehiclePhotoBlock,
  MonthlyIncomeCol,
  ApprovedPaymentCol,
  AvailableDealStructuresBlock,
  SuccessSubmitRow,
  SuccessSubmitLogo,
  LoanVariablesDialogRow,
  SubmitButton,
  SubmitCol,
  SubmitMessage,
  SubmitMessageCol,
  TitleContainer,
  MoreInfoButton,
  StepNumber,
  StepName,
  StepDescription,
  ClearButton,
  SubmitButtonTooltip,
  FieldDescription,
  AllySubmitRow,
  AllySubmitActions,
  AllyRouteOneUserId,
  AllySubmitBlock,
} from '../styles';
import {
  MONTHLY_INCOME_FIELD,
  MONTHLY_INCOME_LABEL,
  PAYMENT_LABEL,
  LENDER_PAYMENT_LABEL,
  ALLY_LINE_3_MW_LABEL,
  ALLY_TOTAL_AMOUNT_FINANCED_MW_LABEL,
  CURRENT_PAYMENT_MW_LABEL,
  TOTAL_AMOUNT_FINANCED_MW_LABEL,
  LENDER_PAYMENT_FIELD,
  PAYMENT_FIELD,
  LENDER_MW_ROUTE_ONE_USER_ID_FIELD,
  BALLOON_PAYMENT_MW_LABEL,
  CREATE_ADDENDUM_FIELD,
  SUBMIT_DEAL_FIELD,
  CONTRACT_DATE_FIELD,
  FIRST_PAYMENT_DATE_FIELD,
} from '../consts/fieldConfig.const';
import { Table } from '../Table/Table';
import { LOAN_VARIABLES_MODAL_FIELDS } from '../common.const';
import { Loader } from '../Loader';
import { IDealSourceData, IDealSourceDataFlags } from '../../../../modules/core/types/dealSource';
import { IAvailableDealStructureStatus } from '../types';
import { ExtraCashUnavailableWarn } from './common/styles';
import { LOAN_VARIABLES_FIELDS_NAMES } from '../consts/fieldNames.const';
import { AfgLogo, AfgMessage, BalloonToggleContainer } from '../balloonPayment/styles';
import { BalloonPaymentRow } from '../balloonPayment/BalloonPaymentRow';
import { VehiclesDescription } from './VehiclesDescription/VehiclesDescription';
import { CheckboxGroup } from './common/CheckboxGroup/CheckboxGroup';
import { LENDER_LIST_COLUMN_COUNT, MAX_SELECTED_OPTIONS_FOR_LENDER_LIST } from '../lenderList/LenderList.const';
import { AvailableDealStructureTitle } from './AvailableDealStructureTitle';
import { SequentialProgressBar } from './SequentialProgressBar/SequentialProgressBar';

const MAX_PAGE_SIZE = 4;
const CURRENT_PAGE = 0;

interface ILoanVariablesFields {
  columns: TableColumn[];
  loanVariables: any;
  showSuccessSubmit: boolean;
  generatedFields: {
    firstRowFields: (JSX.Element | null)[];
    secondRowFields: (JSX.Element | null)[];
    thirdRowFields: (JSX.Element | null)[];
    balloonFields: (JSX.Element | null)[];
    firstRowLabels: (JSX.Element | null)[];
    secondRowLabels: (JSX.Element | null)[];
    thirdRowLabels: (JSX.Element | null)[];
    balloonLabels: (JSX.Element | null)[];
  };
  tier: string;
  generateFields: (fields: IField[]) => (JSX.Element | null)[];
  manuallyCreated?: boolean;
  isLender?: boolean;
  isAdjustDealModalAnimated?: boolean;
  dealSourceData: IDealSourceData;
  dealSourceDataFlags: IDealSourceDataFlags;
  availableDealStructureStatus: IAvailableDealStructureStatus;
  isMakeItWork: boolean;
  isRenderRouteOneUserId: boolean;
  isSubmitButtonDisabled: boolean;
  isShowExtraCashWarn: boolean;
  maxAmountFinancedWarn?: string;
  handleSubmitModalClick: Function;
  resetLoanVariablesFormValues: Function;
  isSubmitR1DealBlockedByControls?: boolean;
  isBalloonPaymentAvailable: boolean;
  isEligibleForAfg: boolean;
  balloonToggle: boolean;
  balloonToggleCallback: ChangeEventHandler;
  isBalloonRowAnimated: boolean;
  isFetchingBalloonResidualValues: boolean;
  residualFetchFailed: boolean;
  isSalespersonLimitedView?: boolean;
  getActualPaymentInfoMessage?: (loanVariables: any, availableDealStructureStatus?: any) => string;
  getLtvCategories?: (loanVariables: any, availableDealStructureStatus: any) => any[];
  lenderListOptions?: IOption[];
  isShowLenderList: boolean;
  isValidLenderList: boolean;
  isClearLenderListButtonDisabled: boolean;
  clearLenderList?: MouseEventHandler;
  lenderListChangeCallback?: (event: any) => void;
  validateModalBeforeSubmit: Function;
  isDisplayPolicyStatusMessage: boolean;
  createAddendum: boolean;
  afgSubmitDeal: boolean;
  isRenderAddendumFields: boolean;
  addendumStep: number;
  addendumStatus?: AddendumStatus;
  shouldPromptResubmit?: boolean;
  isAllyLender?: boolean;
  isResubmitForPricing?: boolean;
  canSubmitDealStructure: boolean;
}

const PAGE_STYLE = `
@page { size: auto; }
@media print {
  body { -webkit-print-color-adjust: exact; }
  .row { --bs-gutter-x: 1.5rem; --bs-gutter-y: 0; display: flex; flex-wrap: wrap; }
  #monthlyIncomeCol { flex: 0 0 37%; flex-direction: column; }
  #approvedPaymentCol { flex: 0 0 37%; flex-direction: column; }
  .col-lg-8 { flex: 0 0 60%; flex-direction: column; }
  .col-md-8 { flex: 0 0 60%; flex-direction: column; }
  .col-lg-9 { flex: 0 0 75%; }
  .button-print { visibility: hidden }
}`;

export const LoanVariablesFields: FC<ILoanVariablesFields> = ({
  columns,
  loanVariables,
  showSuccessSubmit,
  generatedFields,
  tier,
  generateFields,
  manuallyCreated = false,
  isLender = false,
  isAdjustDealModalAnimated = true,
  dealSourceData,
  dealSourceDataFlags,
  availableDealStructureStatus,
  isMakeItWork,
  isRenderRouteOneUserId,
  isSubmitButtonDisabled,
  isShowExtraCashWarn,
  maxAmountFinancedWarn = '',
  handleSubmitModalClick,
  resetLoanVariablesFormValues,
  isSubmitR1DealBlockedByControls = false,
  isBalloonPaymentAvailable,
  isEligibleForAfg,
  balloonToggle,
  balloonToggleCallback,
  isBalloonRowAnimated,
  isFetchingBalloonResidualValues,
  residualFetchFailed,
  isSalespersonLimitedView = false,
  getActualPaymentInfoMessage,
  getLtvCategories,
  lenderListOptions = [],
  isShowLenderList = false,
  isValidLenderList = true,
  isClearLenderListButtonDisabled = false,
  clearLenderList,
  lenderListChangeCallback,
  validateModalBeforeSubmit,
  isDisplayPolicyStatusMessage,
  createAddendum,
  afgSubmitDeal,
  isRenderAddendumFields,
  addendumStep,
  addendumStatus = AddendumStatus.INACTIVE,
  shouldPromptResubmit = false,
  isAllyLender = false,
  isResubmitForPricing = false,
  canSubmitDealStructure,
}) => {
  const { t } = useTranslation();
  const isAmeriCreditLender = useIsAmeriCreditLender(isLender);
  const isMtbLender = useIsMtbLender(isLender);
  const showsLineThree = isAllyLender || isMtbLender;
  const usesApprovedPaymentLabel = isAllyLender || isAmeriCreditLender || isMtbLender;
  const componentRef = useRef<any>(null);
  const promiseResolveRef = useRef<any>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const isCostMissing = useSelector(getIsMissingCostInfo);
  const user = useSelector(getUser) as IUser;
  const currentColumns = isCostMissing
    ? columns.filter((column) => column.id !== LOAN_VARIABLES_MODAL_FIELDS.PROFIT)
    : columns;
  const defaultVehiclePhoto = useSelector(getDefaultVehiclePhoto);
  const controlImages = useSelector(getControlImages);
  const [showContractInfo, setShowContractInfo] = useState(false);

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const isDealerTrack = user?.dealSource === DealSourceProvider.DEALER_TRACK;

  const canSubmitDeal = useMemo(() => isMakeItWork && !isSubmitR1DealBlockedByControls && !isDealerTrack, [
    isMakeItWork,
    isSubmitR1DealBlockedByControls,
    isDealerTrack,
  ]);

  const isMissingRouteOneAppInSbcMode = useMemo(() => isMakeItWork && !dealSourceData?.conversation_id, [
    isMakeItWork,
    dealSourceData?.conversation_id,
  ]);

  const getButtonText = useCallback(() => {
    if (!canSubmitDealStructure) return availableDealStructureStatus.buttonText;

    if (isRenderAddendumFields && createAddendum && !afgSubmitDeal)
      return t('components.loanVariablesModal.buttons.downloadAddendum');

    if (canSubmitDeal) return t('components.loanVariablesModal.buttons.submitDeal');

    return t('components.loanVariablesModal.buttons.print');
  }, [
    canSubmitDealStructure,
    availableDealStructureStatus.buttonText,
    isRenderAddendumFields,
    createAddendum,
    afgSubmitDeal,
    t,
    canSubmitDeal,
  ]);

  const submitPanelTitle = useMemo(() => {
    if (canSubmitDeal) {
      if (isRenderAddendumFields) return t('components.loanVariablesModal.labels.finalizeDeal');

      return t(
        isResubmitForPricing
          ? 'components.loanVariablesModal.labels.resubmitForUpdatedDecision'
          : 'components.loanVariablesModal.labels.submitDeal',
      );
    }

    return t('components.loanVariablesModal.labels.printDeal');
  }, [canSubmitDeal, t, isRenderAddendumFields, isResubmitForPricing]);

  const addendumSteps = useMemo(() => {
    const steps = [
      t('components.loanVariablesModal.progressBar.creatingAddendum'),
      t('components.loanVariablesModal.progressBar.downloadingAddendum'),
    ];

    if (afgSubmitDeal) {
      steps.push(t('components.loanVariablesModal.progressBar.sendingToRouteOne'));
    }

    return steps;
  }, [t, afgSubmitDeal]);

  const getSubmitBlockMessage = useCallback(() => {
    if (dealSourceDataFlags?.message.length > 0) {
      return dealSourceDataFlags.message;
    }

    if (!canSubmitDealStructure) {
      if (!isResubmitForPricing) {
        return t('components.loanVariablesModal.infoMessages.adjustToContinue');
      }

      return canSubmitDeal ? '' : t('components.loanVariablesModal.infoMessages.callForApproval');
    }

    if (canSubmitDeal) {
      if (isRenderAddendumFields && createAddendum && !afgSubmitDeal) {
        return isRenderRouteOneUserId
          ? t('components.loanVariablesModal.infoMessages.addOrConfirmDownloadAddendum')
          : t('components.loanVariablesModal.infoMessages.downloadAddendum');
      }

      if (isRenderAddendumFields && createAddendum && afgSubmitDeal) {
        return isRenderRouteOneUserId
          ? t('components.loanVariablesModal.infoMessages.addOrConfirmWithAddendum')
          : t('components.loanVariablesModal.infoMessages.confirmByClickingWithAddendum');
      }

      if (isRenderAddendumFields) {
        return isRenderRouteOneUserId
          ? t('components.loanVariablesModal.infoMessages.addOrConfirmToCreateAddendum')
          : t('components.loanVariablesModal.infoMessages.confirmByClickingToCreateAddendum');
      }

      if (isResubmitForPricing) {
        return '';
      }

      return isRenderRouteOneUserId
        ? t('components.loanVariablesModal.infoMessages.addOrConfirm')
        : t('components.loanVariablesModal.infoMessages.confirmByClicking');
    }

    return t('components.loanVariablesModal.infoMessages.callForApproval');
  }, [
    dealSourceDataFlags.message,
    canSubmitDealStructure,
    canSubmitDeal,
    t,
    isRenderRouteOneUserId,
    isRenderAddendumFields,
    createAddendum,
    afgSubmitDeal,
    isResubmitForPricing,
  ]);

  const handleShowContractInfo = useCallback(() => {
    setShowContractInfo((prev) => !prev);
  }, []);

  const getLoanVariablesFields = (isPrintView: boolean) => {
    let stepNumber = 0;
    const submitBlockMessage = getSubmitBlockMessage();
    const routeOneUserIdField =
      isRenderRouteOneUserId && addendumStatus !== AddendumStatus.ACTIVE
        ? generateFields([LENDER_MW_ROUTE_ONE_USER_ID_FIELD])
        : null;
    const submitAction = canSubmitDeal ? (
      <div data-tooltip-id="submit-button-tooltip">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <SubmitButton
            buttontheme={ButtonVariant.SUBMIT}
            disabled={isSubmitButtonDisabled}
            onClick={handleSubmitModalClick}
          >
            {getButtonText()}
          </SubmitButton>
        </div>
        {isMissingRouteOneAppInSbcMode && canSubmitDealStructure && (
          <SubmitButtonTooltip id="submit-button-tooltip" place="top" isOpen>
            {t('components.loanVariablesModal.infoMessages.closeWindowToAddRouteOneApp')}
          </SubmitButtonTooltip>
        )}
      </div>
    ) : (
      <ReactToPrint
        content={() => componentRef.current}
        pageStyle={PAGE_STYLE}
        trigger={() => (
          <SubmitButton buttontheme={ButtonVariant.SUBMIT} disabled={isSubmitButtonDisabled}>
            {getButtonText()}
          </SubmitButton>
        )}
        onBeforeGetContent={() =>
          new Promise((resolve, reject) => {
            if (!validateModalBeforeSubmit()) {
              reject();
            } else {
              promiseResolveRef.current = resolve;
              setIsPrinting(true);
            }
          })
        }
        onAfterPrint={() => {
          promiseResolveRef.current = null;
          setIsPrinting(false);
          resetLoanVariablesFormValues();
        }}
      />
    );

    return (
      <LoanVariablesDialogRow $isPrintView={isPrintView} ref={isPrintView ? componentRef : undefined}>
        <Panel>
          <PanelTitle>{t('components.loanVariablesModal.labels.contractInformation')}</PanelTitle>
          <Row>
            <VehiclePhotoBlock className="align-items-center">
              <ImgContainer>
                <Img
                  testId="image"
                  src={getVehiclePhotoSrc(
                    loanVariables.id,
                    user,
                    dealSourceData,
                    manuallyCreated,
                    defaultVehiclePhoto,
                    loanVariables.photo,
                  )}
                  onErrorPhoto={defaultVehiclePhoto}
                />
              </ImgContainer>
            </VehiclePhotoBlock>
            <Col lg={ColumnWidth.XL} md={ColumnWidth.XL} xs={ColumnWidth.L}>
              <Row>
                <Col lg={ColumnWidth.FULL} md={ColumnWidth.FULL} xs={ColumnWidth.FULL}>
                  <VehiclesDescription
                    loanVariables={loanVariables}
                    showContractInfo={showContractInfo}
                    user={user}
                    manuallyCreated={manuallyCreated}
                    dealSourceData={dealSourceData}
                    tier={tier}
                  />
                </Col>
                <MonthlyIncomeCol id="monthlyIncomeCol">
                  <VehiclesDescriptionFields>
                    {isPrintView ? generateFields([MONTHLY_INCOME_LABEL]) : generateFields([MONTHLY_INCOME_FIELD])}
                  </VehiclesDescriptionFields>
                </MonthlyIncomeCol>
                <ApprovedPaymentCol id="approvedPaymentCol">
                  <VehiclesDescriptionFields>
                    {isPrintView
                      ? generateFields([isLender && !usesApprovedPaymentLabel ? LENDER_PAYMENT_LABEL : PAYMENT_LABEL])
                      : generateFields([isLender && !usesApprovedPaymentLabel ? LENDER_PAYMENT_FIELD : PAYMENT_FIELD])}
                  </VehiclesDescriptionFields>
                </ApprovedPaymentCol>
              </Row>
            </Col>
          </Row>
          <MoreInfoButton rotate180={showContractInfo} onClick={handleShowContractInfo}>
            {showContractInfo
              ? t('components.loanVariablesModal.labels.lessInfo')
              : t('components.loanVariablesModal.labels.moreInfo')}
            <img alt={''} src={STATIC_IMAGE_URLS.icons.caretDown} />
          </MoreInfoButton>
        </Panel>
        <Panel title={t('components.RACAffordabilityEngine.approvedLoanTerms.title')}>
          <PanelTitle>
            <TitleContainer>
              <StepNumber>{t('components.loanVariablesModal.labels.step', { stepNumber: ++stepNumber })}</StepNumber>
              <StepName>{t('components.loanVariablesModal.labels.optimizeDeal')}</StepName>
              {isBalloonPaymentAvailable && (
                <BalloonToggleContainer isAvailable={isEligibleForAfg || balloonToggle}>
                  <AfgLogo src={controlImages.afgLogo} />
                  <Toggle
                    icons={false}
                    name={LOAN_VARIABLES_FIELDS_NAMES.BALLOON_TOGGLE}
                    changeCallback={balloonToggleCallback}
                    checked={balloonToggle}
                    disabled={
                      (!isEligibleForAfg && !balloonToggle) ||
                      isAdjustDealModalAnimated ||
                      addendumStatus === AddendumStatus.ACTIVE
                    }
                  />
                </BalloonToggleContainer>
              )}
            </TitleContainer>
            {isBalloonPaymentAvailable && !isEligibleForAfg && (
              <AfgMessage>{t('components.loanVariablesModal.infoMessages.notEligibleForAfg')}</AfgMessage>
            )}
          </PanelTitle>
          {balloonToggle && (
            <BalloonPaymentRow
              isPrintView={isPrintView}
              generateFields={generateFields}
              generatedFields={generatedFields}
              isBalloonRowAnimated={isBalloonRowAnimated}
              isFetchingBalloonResidualValues={isFetchingBalloonResidualValues}
              residualFetchFailed={residualFetchFailed}
            />
          )}
          <div>
            <FieldsRow>{isPrintView ? generatedFields.firstRowLabels : generatedFields.firstRowFields}</FieldsRow>
            <FieldsRow>{isPrintView ? generatedFields.secondRowLabels : generatedFields.secondRowFields}</FieldsRow>
            <FieldsRow>
              {isPrintView ? generatedFields.thirdRowLabels : generatedFields.thirdRowFields}
              <PaymentCol>
                <PaymentRow $showBalloonPayment={balloonToggle} $showExtraRow={showsLineThree}>
                  <Col xs={ColumnWidth.FULL} className="d-flex align-items-center">
                    {generateFields([CURRENT_PAYMENT_MW_LABEL])}
                  </Col>
                  {balloonToggle && (
                    <Col xs={ColumnWidth.FULL} className="d-flex align-items-center">
                      {generateFields([BALLOON_PAYMENT_MW_LABEL])}
                    </Col>
                  )}
                  {/* Ally and M&T split this in two: Line 3 from the lender, then Line 3 + Back Adds. */}
                  <Col xs={ColumnWidth.FULL} className="d-flex align-items-center">
                    {generateFields([showsLineThree ? ALLY_LINE_3_MW_LABEL : TOTAL_AMOUNT_FINANCED_MW_LABEL])}
                  </Col>
                  {showsLineThree && (
                    <Col xs={ColumnWidth.FULL} className="d-flex align-items-center">
                      {generateFields([ALLY_TOTAL_AMOUNT_FINANCED_MW_LABEL])}
                    </Col>
                  )}
                </PaymentRow>
              </PaymentCol>
            </FieldsRow>
          </div>
        </Panel>
        {!isAdjustDealModalAnimated && (
          <Panel>
            <AvailableDealStructuresBlock>
              <AvailableDealStructureTitle
                availableDealStructureStatus={availableDealStructureStatus}
                isDisplayPolicyStatusMessage={isDisplayPolicyStatusMessage}
                stepNumber={++stepNumber}
                shouldPromptResubmit={shouldPromptResubmit}
                isResubmitForPricing={isResubmitForPricing}
              />
              <Table
                pageSize={MAX_PAGE_SIZE}
                currentPage={CURRENT_PAGE}
                columns={currentColumns}
                ltvCategories={getLtvCategories ? getLtvCategories(loanVariables, availableDealStructureStatus) : []}
                availableDealStructureStatus={availableDealStructureStatus}
              />
              <ActualPaymentInfoTitle $isResubmitForPricing={isResubmitForPricing}>
                {getActualPaymentInfoMessage
                  ? getActualPaymentInfoMessage(loanVariables, availableDealStructureStatus)
                  : ''}
              </ActualPaymentInfoTitle>
            </AvailableDealStructuresBlock>
            {isShowExtraCashWarn && (!isResubmitForPricing || Boolean(maxAmountFinancedWarn)) && (
              <div className="d-flex flex-column align-items-end">
                <ExtraCashUnavailableWarn>
                  {maxAmountFinancedWarn || t('components.loanVariablesModal.warningMessages.extraCashUnavailable')}
                </ExtraCashUnavailableWarn>
              </div>
            )}
          </Panel>
        )}
        {isShowLenderList && !isPrintView && !isAdjustDealModalAnimated && (
          <Panel>
            <PanelTitle>
              <TitleContainer $isInvalid={!isValidLenderList}>
                <StepNumber>{t('components.loanVariablesModal.labels.step', { stepNumber: ++stepNumber })}</StepNumber>
                <span>
                  <StepName>{t('components.loanVariablesModal.labels.selectLenders')}</StepName>
                  <StepDescription>{t('components.loanVariablesModal.labels.lenderListRange')}</StepDescription>
                </span>
                <ClearButton disabled={isClearLenderListButtonDisabled} onClick={clearLenderList}>
                  {t('components.loanVariablesModal.buttons.clear')}
                </ClearButton>
              </TitleContainer>
            </PanelTitle>
            <CheckboxGroup
              name={LOAN_VARIABLES_FIELDS_NAMES.LENDER_LIST}
              options={lenderListOptions}
              changeCallback={lenderListChangeCallback}
              columnCount={LENDER_LIST_COLUMN_COUNT}
              maxSelectedOptions={MAX_SELECTED_OPTIONS_FOR_LENDER_LIST}
            />
          </Panel>
        )}
        {!isPrintView && !isAdjustDealModalAnimated && isRenderAddendumFields && (
          <Panel>
            <PanelTitle>
              <TitleContainer>
                <StepNumber>{t('components.loanVariablesModal.labels.step', { stepNumber: ++stepNumber })}</StepNumber>
                <StepName>{t('components.loanVariablesModal.labels.afgAddendum')}</StepName>
              </TitleContainer>
            </PanelTitle>
            <div>
              {generateFields([CREATE_ADDENDUM_FIELD])}
              <FieldDescription>{t('components.loanVariablesModal.infoMessages.createAddendum')}</FieldDescription>
              {createAddendum && (
                <>
                  {generateFields([SUBMIT_DEAL_FIELD])}
                  <FieldDescription>{t('components.loanVariablesModal.infoMessages.submitDeal')}</FieldDescription>
                  <Row>
                    <Col xs={ColumnWidth.XXS}>{generateFields([CONTRACT_DATE_FIELD])}</Col>
                    <Col xs={ColumnWidth.XXS}>{generateFields([FIRST_PAYMENT_DATE_FIELD])}</Col>
                  </Row>
                </>
              )}
            </div>
          </Panel>
        )}
        {isAdjustDealModalAnimated && (
          <Panel>
            <Loader isWithMargins={false} />
          </Panel>
        )}
        {!isPrintView && !isAdjustDealModalAnimated && (
          <Panel>
            {dealSourceDataFlags?.isLoading && (!isRenderAddendumFields || !createAddendum) ? (
              <>
                <PanelTitle>
                  <TitleContainer>
                    <StepNumber>
                      {t('components.loanVariablesModal.labels.step', { stepNumber: ++stepNumber })}
                    </StepNumber>
                    <StepName>{submitPanelTitle}</StepName>
                  </TitleContainer>
                </PanelTitle>
                <Loader isWithMargins={false} />
              </>
            ) : isResubmitForPricing && canSubmitDeal && !isRenderAddendumFields && !submitBlockMessage ? (
              <AllySubmitBlock>
                <AllySubmitRow data-testid="ally-submit-row">
                  <PanelTitle $compact>
                    <TitleContainer>
                      <StepNumber>
                        {t('components.loanVariablesModal.labels.step', { stepNumber: ++stepNumber })}
                      </StepNumber>
                      <StepName>{submitPanelTitle}</StepName>
                    </TitleContainer>
                  </PanelTitle>
                  <AllySubmitActions>
                    {routeOneUserIdField && <AllyRouteOneUserId>{routeOneUserIdField}</AllyRouteOneUserId>}
                    {submitAction}
                  </AllySubmitActions>
                </AllySubmitRow>
              </AllySubmitBlock>
            ) : (
              <>
                <PanelTitle>
                  <TitleContainer>
                    <StepNumber>
                      {t('components.loanVariablesModal.labels.step', { stepNumber: ++stepNumber })}
                    </StepNumber>
                    <span>
                      <StepName>{submitPanelTitle}</StepName>
                      {isRenderAddendumFields && createAddendum && addendumStep > 0 && (
                        <StepDescription>
                          {t('components.loanVariablesModal.infoMessages.addendumDescription')}
                        </StepDescription>
                      )}
                    </span>
                  </TitleContainer>
                </PanelTitle>
                <Row className="justify-content-end" data-testid="submit-row">
                  {isRenderAddendumFields ? (
                    <>
                      {addendumStep > 0 && (
                        <SequentialProgressBar
                          stepLabels={addendumSteps}
                          activeStep={addendumStep}
                          isFailed={addendumStatus === AddendumStatus.FAILED}
                        />
                      )}
                      {(addendumStep === 0 || dealSourceDataFlags?.isInvalid) && submitBlockMessage && (
                        <SubmitMessage isInvalid={!!dealSourceDataFlags?.isInvalid}>{submitBlockMessage}</SubmitMessage>
                      )}
                    </>
                  ) : (
                    submitBlockMessage && (
                      <SubmitMessageCol data-testid="submit-message-col">
                        <SubmitMessage isInvalid={!!dealSourceDataFlags?.isInvalid}>{submitBlockMessage}</SubmitMessage>
                      </SubmitMessageCol>
                    )
                  )}
                  {routeOneUserIdField && <SubmitCol>{routeOneUserIdField}</SubmitCol>}
                  <SubmitCol>{submitAction}</SubmitCol>
                </Row>
              </>
            )}
          </Panel>
        )}
      </LoanVariablesDialogRow>
    );
  };

  return showSuccessSubmit ? (
    <SuccessSubmitRow>
      <div className="fw-bold mb-4">{t('components.loanVariablesModal.successSubmit.success')}</div>
      <div className="fw-bold">{t('components.loanVariablesModal.successSubmit.goBackInRouteOne')}</div>
      <SuccessSubmitLogo
        src={STATIC_IMAGE_URLS.icons.successSubmit}
        alt={t('components.loanVariablesModal.successSubmit.successSubmitLogo')}
      />
      <div>{t('components.loanVariablesModal.successSubmit.autoCloseWindow')}</div>
    </SuccessSubmitRow>
  ) : (
    <>
      {isPrinting && <div style={{ display: 'none' }}>{getLoanVariablesFields(true)}</div>}
      {getLoanVariablesFields(false)}
    </>
  );
};
